<template>
    <div class="flex flex-column h-screen authenticatedlayoutnew">
        <!-- Loading states are handled by individual router-view components -->
        <AuthenticatedHeader />
        <!-- min-h-0 keeps tall pages from stretching the sidebar (and its gradient); content scrolls instead. -->
        <div
            class="flex flex-row flex-grow-1 min-h-0"
            :style="[
                route.meta.mapView ? { height: 'calc(100vh - 60px)' } : {},
            ]">
            <div
                class="flex flex-column flex-shrink-0 bg-sidebar block static left-0 top-0 select-none overflow-y-auto transition-all transition-duration-150"
                :style="{
                    width: sidebarOpen ? '200px' : '70px',
                    'z-index': '100',
                }">
                <Navigation />
                <MqttLagIndicator v-if="lagIndicatorVisible" class="mt-auto" />
            </div>
            <div class="flex flex-column flex-grow-1 min-h-0 overflow-y-auto">
                <router-view />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStorage, onKeyStroke } from "@vueuse/core";

import AuthenticatedHeader from "@/components/AuthenticatedHeader.vue";
import Navigation from "@/components/Navigation.vue";
import MqttLagIndicator from "@/components/MqttLagIndicator.vue";

import { useAuthenticationStore } from "@/stores/authentication";
import { useStaticDataStore } from "@/stores/staticData";
import { useViewContextStore } from "@/stores/viewContext";
import { useVenueDataStore } from "@/stores/venueData";
import { useVMSStore } from "@/stores/vms";
import { useInterfaceProperties } from "@/composables/useInterfaceProperties.js";
import { useStreamService } from "@/composables/useStreamService";
import { EnvironmentService } from "@/utils/environment";

const route = useRoute();
const router = useRouter();
const authStore = useAuthenticationStore();
const staticDataStore = useStaticDataStore();
const viewContext = useViewContextStore();
const venueDataStore = useVenueDataStore();
const vmsStore = useVMSStore();
const { sidebarOpen } = useInterfaceProperties();
const streamService = useStreamService();

// Hidden by default; toggled with Ctrl/Cmd+Shift+L. Persisted across reloads.
const lagIndicatorVisible = useStorage("edge-lag-indicator-visible", false);
onKeyStroke("L", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault();
        lagIndicatorVisible.value = !lagIndicatorVisible.value;
    }
});

/** Prevents the route watcher from acting during initial data load */
const dataInitialized = ref(false);

/** Sync operator/installer mode from the matched route meta to viewContext */
function syncModeFromRoute() {
    const modeParam = route.matched.find((r) => r.meta.mode)?.meta.mode;
    const newMode = modeParam === "installer" ? "installer" : "operator";
    if (newMode !== viewContext.mode) {
        viewContext.setMode(newMode);
    }
}

/**
 * Resolve and validate the venue ID from the current route.
 * If the requested venue doesn't exist, redirects to the default and returns its ID.
 * Returns null if no venues are available.
 */
function resolveVenueId(): number | null {
    const requestedId = route.params.venueId
        ? Number(route.params.venueId)
        : null;
    const venues = venueDataStore.allVenuesList;

    if (requestedId && venues.some((v) => v.id === requestedId)) {
        return requestedId;
    }

    if (venues.length === 0) return null;

    const defaultVenue = venues.find((v) => v.defaultVenue) ?? venues[0];

    if (route.params.venueId && requestedId !== defaultVenue.id) {
        console.warn(
            `[AuthenticatedLayout] Venue ${requestedId} not found, redirecting to ${defaultVenue.id}`,
        );
        router.replace({
            name: route.name as string,
            params: { ...route.params, venueId: defaultVenue.id },
        });
    }

    return defaultVenue.id;
}

let venueSetupToken = 0;
// Venue a setup is loading; viewContext.venueId only switches once its data is ready
let pendingVenueId: number | null = null;
let unmounted = false;

/** Drop any in-flight venue setup so it can no longer write to viewContext */
function cancelVenueSetup() {
    venueSetupToken++;
    pendingVenueId = null;
}

/** Apply the route's incident and venue to viewContext */
async function syncContextFromRoute() {
    const incidentId = route.params.incidentId;
    viewContext.setIncidentId(
        incidentId && typeof incidentId === "string"
            ? Number(incidentId)
            : null,
    );
    await syncVenueFromRoute();
}

/** Set up the route's venue if it differs from the one shown or loading */
async function syncVenueFromRoute() {
    if (!route.params.venueId) return;

    const venueId = resolveVenueId();
    if (!venueId) return;
    if (venueId === (pendingVenueId ?? viewContext.venueId)) return;

    // Switched back before the pending setup landed — keep the current level
    if (venueId === viewContext.venueId) {
        cancelVenueSetup();
        return;
    }

    await setupVenueContext(venueId);
}

/**
 * Prepare a venue and set it as the active context.
 * Users fetch runs in parallel with venue+level data — on init, MQTT
 * connects only after both are complete.
 *
 * When loading directly to an incident page (incidentId in the route), the
 * incident's level is used instead of the venue default so that markers and
 * end-users are fetched for the correct level from the very first render.
 */
async function setupVenueContext(venueId: number) {
    // Latest call wins — a slower, superseded setup must not overwrite the context
    cancelVenueSetup();
    const token = venueSetupToken;
    pendingVenueId = venueId;

    // Start user fetch immediately — independent of venue basics/level data.
    // Not cancelled on supersede: results are stored per venue and stay useful.
    const usersPromise = venueDataStore.fetchVenueUsers(venueId);

    // prepareVenueForViewing fetches incidents, so after this resolves we can
    // look up the incident's level from the store.
    try {
        await venueDataStore.prepareVenueForViewing(venueId);
    } finally {
        // Clear on failure too, or the route sync would treat this venue as loading forever
        if (token === venueSetupToken) pendingVenueId = null;
    }
    if (token !== venueSetupToken) return;
    viewContext.setVenueId(venueId);

    const levels = venueDataStore.getLevels(venueId);
    if (levels.length > 0) {
        // If we're loading directly to an incident, use the incident's level so
        // markers and end-users are fetched for the right level immediately.
        const routeIncidentId = route.params.incidentId
            ? Number(route.params.incidentId)
            : null;
        const incident = routeIncidentId
            ? venueDataStore.getIncidentById(routeIncidentId)
            : null;
        const incidentLevel = incident?.levelId
            ? levels.find((l) => l.id === incident.levelId)
            : null;

        const defaultLevel = levels.find((l) => l.defaultLevel) ?? levels[0];
        const initialLevel = incidentLevel ?? defaultLevel;

        viewContext.setLevelId(initialLevel.id);
        // Level data and users are independent — fetch concurrently, gate on both
        await Promise.all([
            venueDataStore.fetchLevelData(venueId, initialLevel.id),
            usersPromise,
        ]);
    } else {
        await usersPromise;
    }
}

/** Bootstrap all application data, then connect the MQTT stream */
async function initializeData() {
    dataInitialized.value = false;
    streamService.prefetchConfig();

    try {
        await Promise.all([
            staticDataStore.dataReady
                ? Promise.resolve()
                : staticDataStore.fetchAllStaticData(),
            venueDataStore.initialized
                ? Promise.resolve()
                : venueDataStore.initializeCurrentVenue(),
        ]);
        if (unmounted) return;

        const venueId = resolveVenueId();
        if (!venueId) return;

        await setupVenueContext(venueId);
        // Unmounted mid-init — don't connect a stream nobody will disconnect
        if (unmounted) return;

        // Open the route watcher before MQTT, so a slow broker can't block venue switches.
        // It ignores route changes made during init — catch up now.
        dataInitialized.value = true;
        syncContextFromRoute().catch(console.error);

        // Connect MQTT — user baseline is now loaded, delta queue can safely flush
        await streamService.connect();
        if (unmounted) return;

        // Non-blocking background tasks
        venueDataStore.initializeRemainingVenues(venueId).catch(console.error);
        if (EnvironmentService.isCommunityDomain()) {
            vmsStore.initializeVMS().catch(console.warn);
        }
    } catch (error) {
        console.error("[AuthenticatedLayout] Initialization error:", error);
    }
}

// Must run synchronously before any child renders
syncModeFromRoute();

onMounted(() => {
    if (authStore.isAuthenticated()) {
        initializeData();
    }
});

watch(
    () => [
        route.params.venueId,
        route.params.incidentId,
        route.matched.find((r) => r.meta.mode)?.meta.mode,
    ],
    async () => {
        syncModeFromRoute();
        if (!dataInitialized.value) return;
        await syncContextFromRoute();
    },
);

onUnmounted(() => {
    unmounted = true;
    cancelVenueSetup();
    streamService.disconnect();
});
</script>
